/**
 * PostHog for the app (app.conistra.ai).
 *
 * This exists to close the funnel that the marketing site opens. The landing
 * page at www.conistra.ai already reports `website_*` events; without the app
 * reporting the other end, a landing-page A/B test can only ever measure CTA
 * clicks, never signups. See docs/ab-test-landing-2026.md.
 *
 * Three things here are load-bearing and easy to break:
 *
 *  1. `cross_subdomain_cookie: true` — the marketing site and the app are
 *     different hosts (www.conistra.ai / app.conistra.ai). The cookie has to be
 *     written on `.conistra.ai` or PostHog treats one human as two people and
 *     every cross-domain funnel reads 0%. `frontend/web/analytics.js` sets the
 *     same option; the two must stay in agreement.
 *
 *  2. The project key must match the marketing site's key. Different projects
 *     cannot be joined in a funnel at all.
 *
 *  3. posthog-js is loaded as a LAZY CHUNK, not bundled into main. Statically
 *     importing it costs ~79 kB gzip on the main bundle, which also serves
 *     `/signup` — the exact page whose conversion rate this instrumentation is
 *     meant to measure. Making the measurement slow down the thing being
 *     measured would bias the experiment it exists to support. Calls made before
 *     the chunk lands are queued below, so callers never have to care.
 */
import type { PostHog } from 'posthog-js';

const POSTHOG_KEY =
  process.env.REACT_APP_POSTHOG_KEY || 'phc_psCuaQDkbdKxqv7D7zuixd4ridAqxHGfEoiV4NJYYCVN';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

type Props = Record<string, unknown>;
type QueuedCall = (_client: PostHog) => void;

let client: PostHog | null = null;
let booting = false;
/** Calls made before the lazy chunk resolved. Replayed in order on load. */
let queue: QueuedCall[] = [];

function run(call: QueuedCall): void {
  if (client) {
    try {
      call(client);
    } catch {
      /* analytics must never take the app down */
    }
    return;
  }
  // Bounded so a failed chunk load can't grow this without limit.
  if (queue.length < 50) queue.push(call);
}

/** True once posthog-js has loaded and initialised. */
export const analyticsReady = (): boolean => client !== null;

export function initAnalytics(): void {
  if (booting || client || !POSTHOG_KEY || typeof window === 'undefined') return;
  booting = true;

  import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        defaults: '2026-01-30',
        person_profiles: 'identified_only',
        // See note 1 above — do not remove.
        cross_subdomain_cookie: true,
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
        loaded: (loaded) => {
          loaded.register({
            site_surface: 'frontend_app',
            site_hostname: window.location.hostname,
          });
        },
      });

      client = posthog;
      const pending = queue;
      queue = [];
      pending.forEach((call) => {
        try {
          call(posthog);
        } catch {
          /* swallow */
        }
      });
    })
    .catch(() => {
      // Chunk blocked (adblocker, offline). Drop the queue and stay silent.
      booting = false;
      queue = [];
    });
}

/** Fail-safe capture. Safe to call before init has resolved. */
export function track(event: string, props?: Props): void {
  run((c) => c.capture(event, props));
}

/**
 * Attach the anonymous history — including the marketing-site pageviews that
 * share the .conistra.ai cookie — to a real person.
 */
export function identifyUser(user: {
  id?: number;
  username?: string;
  email?: string;
}): void {
  if (!user) return;
  const distinctId = user.email || user.username || (user.id ? String(user.id) : '');
  if (!distinctId) return;
  run((c) =>
    c.identify(distinctId, {
      email: user.email,
      username: user.username,
      user_id: user.id,
    })
  );
}

export function resetAnalytics(): void {
  run((c) => c.reset());
}
